import './styles.sass';

import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import * as QueryFetcher from 'components/Explorer/models/query_fetcher';
import useQueryParams from 'hooks/useQueryParams';
import FilterSet from 'components/Explorer/models/filter_set';
import HelpLink from 'components/Explorer/AdvancedSearch/HelpLink';
import Button from 'components/Shared/Button';
import { trackEvent as trackAnalyticsEvent } from 'modules/analytics';

import Editor from './Editor';

const DIALOG_TRANSITION_TIME = 250;

const ModalPicker = ({
  filterSet,
  initialSearch,
  i18nKey,
  filterKey,
  autocompletePath,
  childrenPath,
  bootstrapMethod,
  analyticsDialogueName,
  maxSelected,
  helperText,
  renderResultMeta,
  renderResultExtras,
  renderNoResults,
  renderSelectedItemExtras,
  renderSelectedList,
  parseResponse,
  getResultGroups
}) => {
  const params = useQueryParams();
  // Per-instance id so the dialog's accessible name never collides with the
  // other pickers on the page (several ModalPickers render at once).
  const titleId = useId();
  const dialogRef = useRef(null);
  // The transition-tail timeout that flips isOpen back to false. Holding the
  // id lets a fast re-open cancel a pending unmount before it fires.
  const closeTimeoutRef = useRef(null);
  const [selected, setSelected] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);

  const i18nBase = `Explorer.AdvancedSearch.fields.${i18nKey}`;

  const trackEvent = useCallback(
    (eventName, metadata = {}) => {
      trackAnalyticsEvent(`Advanced Search: Modal Dialogue ${eventName}`, {
        dialogue_name: analyticsDialogueName,
        ...metadata
      });
    },
    [analyticsDialogueName]
  );

  useEffect(() => {
    let request;
    let cancelled = false;

    if (window.current_user.librarian) {
      return;
    }

    if (filterSet.get(filterKey).length) {
      setLoading(true);
      request = QueryFetcher.fetchJSON(`filters/${bootstrapMethod}`, params);
      request.then((error, response) => {
        // The cleanup below aborts in-flight requests on a filterSet change
        // or unmount; the resulting AbortError otherwise reaches this handler
        // and either resets state for the next render or warns about a state
        // update on an unmounted component.
        if (cancelled) return;
        // Always reset selected on response so a re-bootstrap that errors or
        // returns no rows can't leave a stale count from a prior filterSet
        // sitting in the trigger row.
        setSelected(!error && response.body?.length ? response.body : []);
        setLoading(false);
      });
    } else {
      setSelected([]);
    }

    return () => {
      cancelled = true;
      request?.abort();
    };
    // Re-bootstrap on filterSet change only (mirrors the other AdvancedSearch
    // filters); `params` is read fresh each run rather than driving the effect,
    // so unrelated navigation (page, sort) doesn't refetch the selection.
  }, [filterSet, filterKey, bootstrapMethod]);

  // Auto-open the picker when a chip-driven initialSearch term is supplied.
  // Waits for the bootstrap fetch (if any) to settle so the dialog opens
  // against the fully-resolved prior selection.
  useEffect(() => {
    if (window.current_user.librarian) return;
    if (!initialSearch || hasAutoOpened || loading) return;

    setHasAutoOpened(true);
    showDialog();
    trackEvent('Auto-opened', { from_term: initialSearch });
  }, [initialSearch, hasAutoOpened, loading]);

  // Clear any pending close-transition timeout on unmount so it can't fire
  // setIsOpen against an unmounted component.
  useEffect(() => () => clearTimeout(closeTimeoutRef.current), []);

  if (window.current_user.librarian) return null;

  function showDialog() {
    // Cancel any pending unmount from a recently-closed instance so the Editor
    // we're about to expose doesn't get torn down mid-life.
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsOpen(true);
    // HTMLDialogElement.showModal throws InvalidStateError if the dialog is
    // already open. Guard against re-entry — possible during fast double-fires
    // of the trigger or if openEditor and the auto-open effect race.
    if (!dialogRef.current?.open) {
      dialogRef.current?.showModal();
    }
  }

  function openEditor() {
    showDialog();
    trackEvent('Opened');
  }

  function closeEditor() {
    dialogRef.current?.close();
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      closeTimeoutRef.current = null;
    }, DIALOG_TRANSITION_TIME);
  }

  function cancel() {
    closeEditor();
    trackEvent('Closed', { method: 'Cancel button' });
  }

  function save(items) {
    setSelected(items);
    if (items.length === 0) {
      filterSet.clear(filterKey);
    } else {
      filterSet.set(
        filterKey,
        items.map((item) => item.id)
      );
    }
    closeEditor();
    trackEvent('Saved', { items_count: items.length });
  }

  function clear() {
    filterSet.clear(filterKey);
    setSelected([]);
    trackEvent('Cleared');
  }

  return (
    <div className="ModalPicker Explorer-AdvancedSearch-field">
      <fieldset className="ModalPicker-fieldset">
        <legend className="Explorer-Form-label ModalPicker-legend">
          {I18n.t(`${i18nBase}.title`)}
          <HelpLink
            href={I18n.t('Explorer.AdvancedSearch.help_uri')}
            tip={I18n.t(`${i18nBase}.help`)}
            analyticsFieldName={analyticsDialogueName}
          />
        </legend>
        <div className="ModalPicker-fieldset-content">
          {loading && <span>{I18n.t(`${i18nBase}.loading`)}</span>}
          {!loading && selected.length > 0 && (
            <>
              <div className="ModalPicker-selected">{I18n.t(`${i18nBase}.count`, { count: selected.length })}</div>
              <Button onClick={openEditor}>{I18n.t(`${i18nBase}.edit`)}</Button>
              <Button onClick={clear} variant="danger">
                {I18n.t(`${i18nBase}.clear`)}
              </Button>
            </>
          )}
          {!loading && selected.length === 0 && <Button onClick={openEditor}>{I18n.t(`${i18nBase}.add`)}</Button>}
        </div>
        <dialog
          ref={dialogRef}
          className="ModalPicker-dialog"
          closedby="closerequest"
          aria-labelledby={titleId}
          onCancel={() => {
            trackEvent('Closed', { method: 'Escape Key Press' });
            // The browser has already closed the dialog by the time onCancel
            // fires. closeEditor's dialog.close() is a harmless no-op here;
            // what we need is the scheduled setIsOpen(false) so the Editor
            // tears down to match.
            closeEditor();
          }}
          onKeyDown={(event) => event.stopPropagation()}
        >
          {isOpen && (
            <Editor
              initiallySelected={selected}
              initialSearch={initialSearch}
              titleId={titleId}
              i18nBase={i18nBase}
              analyticsDialogueName={analyticsDialogueName}
              autocompletePath={autocompletePath}
              childrenPath={childrenPath}
              trackEvent={trackEvent}
              onCancel={cancel}
              onSave={save}
              maxSelected={maxSelected}
              helperText={helperText}
              renderResultMeta={renderResultMeta}
              renderResultExtras={renderResultExtras}
              renderNoResults={renderNoResults}
              renderSelectedItemExtras={renderSelectedItemExtras}
              renderSelectedList={renderSelectedList}
              parseResponse={parseResponse}
              getResultGroups={getResultGroups}
            />
          )}
        </dialog>
      </fieldset>
    </div>
  );
};

ModalPicker.propTypes = {
  filterSet: PropTypes.instanceOf(FilterSet).isRequired,
  initialSearch: PropTypes.string,
  i18nKey: PropTypes.string.isRequired,
  filterKey: PropTypes.string.isRequired,
  autocompletePath: PropTypes.string.isRequired,
  // Endpoint paging an organisation's descendants for lazy tree expansion.
  // Omitted by consumers whose results have no hierarchy.
  childrenPath: PropTypes.string,
  bootstrapMethod: PropTypes.string.isRequired,
  analyticsDialogueName: PropTypes.string.isRequired,
  // Pass null to disable the selection cap.
  maxSelected: PropTypes.number,
  // Optional helper-text lines rendered beneath the field label.
  helperText: PropTypes.arrayOf(PropTypes.node),
  // Optional render-prop returning extra content inside each result row's
  // text container (e.g. an affiliation subline under the name).
  renderResultMeta: PropTypes.func,
  // Optional render-prop returning content rendered as a sibling of the
  // label inside each result row (e.g. an external profile link).
  renderResultExtras: PropTypes.func,
  // Optional render-prop receiving the current search term and returning
  // the empty-results content (e.g. researcher's bolded-term variant).
  // Defaults to the i18n no_results key.
  renderNoResults: PropTypes.func,
  // Optional render-prop returning content rendered alongside each entry
  // in the expanded SelectedList (e.g. researcher's per-item profile link).
  renderSelectedItemExtras: PropTypes.func,
  // Escape hatch that replaces the entire SelectedList region. See the
  // detailed note on this prop in Editor.jsx. Pre-harmonisation, only the
  // researcher consumer supplies one — to preserve its inline selected list
  // (h2 heading + empty-state paragraph) while waiting for product to sign
  // off on harmonising the selection UI across all filters.
  renderSelectedList: PropTypes.func,
  // Optional adapter turning the raw autocomplete body into { results, meta }.
  // Defaults to treating the body as the bare results array. The researcher
  // consumer supplies one to unwrap its { organisation_name, researchers }
  // envelope and carry the org name through as meta.
  parseResponse: PropTypes.func,
  // Optional partitioner splitting the visible rows into labelled sections.
  // Receives the indexed rows and parsed meta; returns ordered
  // [{ key, label, rows }] or null for a flat list. The researcher consumer
  // uses it to float own-organisation matches into a leading section.
  getResultGroups: PropTypes.func
};

export default ModalPicker;
